import { useState, useEffect } from 'react';
import { Form, Input, App, Tabs, Alert, Radio } from 'antd';
import { trpc } from '../../../lib/api';
import { withDevModalFilePath, NumericInput, PhoneInput, PostalCodeInput, EmailInput } from '../../../components/common';
import { DevEmailPreviewModal } from '../../../components/dev/DevEmailPreviewModal';
import { useFormAutoBackup } from '../../../hooks/useFormAutoBackup';

// 開発環境かどうかを判定
const isDevelopment = import.meta.env.DEV;

interface CreateAgencyModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

type DevEmailData = {
  to: string;
  name: string;
  subject: string;
  html: string;
  setupUrl: string;
  token: string;
  expiresAt: string;
  sentAt: string;
};

const { Modal } = withDevModalFilePath('client/src/pages/admin/agencies/CreateAgencyModal.tsx');

export function CreateAgencyModal({ open, onCancel, onSuccess }: CreateAgencyModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  // フォーム自動バックアップ（新規作成のみなので entityId は null）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({
    formType: 'agency',
    entityId: null,
    form,
    enabled: open,
  });

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  // 開発環境用: 最後に送信されたメール内容を取得
  const devEmailQuery = trpc.agencyInvitation.getLastDevEmail.useQuery(undefined, {
    enabled: false, // 手動でフェッチする
  });

  const createMutation = trpc.agencyAdmin.createAgencyUser.useMutation({
    onSuccess: async () => {
      message.success('代理店を作成しました');
      form.resetFields();
      clearBackup(); // 保存成功時にバックアップをクリア

      // 開発環境の場合、メールプレビューを取得して表示
      if (isDevelopment) {
        try {
          await devEmailQuery.refetch();
          setEmailPreviewOpen(true);
        } catch {
          // エラーは無視（開発環境機能なので）
        }
      }

      onSuccess();
    },
    onError: (error) => {
      message.error(error.message || '代理店の作成に失敗しました');
    },
  });

  const devEmailData: DevEmailData | null =
    devEmailQuery.data &&
    typeof devEmailQuery.data.to === 'string' &&
    typeof devEmailQuery.data.name === 'string' &&
    typeof devEmailQuery.data.subject === 'string' &&
    typeof devEmailQuery.data.html === 'string' &&
    typeof devEmailQuery.data.setupUrl === 'string' &&
    typeof devEmailQuery.data.token === 'string' &&
    typeof devEmailQuery.data.expiresAt === 'string' &&
    typeof devEmailQuery.data.sentAt === 'string'
      ? {
          to: devEmailQuery.data.to,
          name: devEmailQuery.data.name,
          subject: devEmailQuery.data.subject,
          html: devEmailQuery.data.html,
          setupUrl: devEmailQuery.data.setupUrl,
          token: devEmailQuery.data.token,
          expiresAt: devEmailQuery.data.expiresAt,
          sentAt: devEmailQuery.data.sentAt,
        }
      : null;

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      createMutation.mutate(values);
    });
  };

  const tabItems = [
    {
      key: 'basic',
      label: '基本情報 *',
      children: (
        <>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <EmailInput placeholder="agency@example.com" />
          </Form.Item>

          <Alert
            message="招待メールによるパスワード設定"
            description="代理店作成後、入力されたメールアドレス宛に初回パスワード設定用の招待メールが自動送信されます。代理店ユーザーはメール内のリンクからパスワードを設定できます。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="couponPrefix"
            label="クーポンプレフィックス"
            normalize={(value) => value?.toUpperCase()}
            rules={[
              { required: true, message: 'クーポンプレフィックスを入力してください' },
              {
                pattern: /^[A-Z0-9]{4,12}$/,
                message: '大文字英字と数字のみ、4文字以上12文字以内で入力してください'
              },
            ]}
            extra="例: AG001（大文字・数字のみ、4-12文字、変更不可、一意である必要があります）"
          >
            <Input
              placeholder="AG001"
              maxLength={12}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="代理店名"
            rules={[{ required: true, message: '代理店名を入力してください' }]}
          >
            <Input placeholder="株式会社XXX代理店" />
          </Form.Item>

          <Form.Item name="companyName" label="会社名（オプション）">
            <Input placeholder="株式会社XXX" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="電話番号"
            rules={[{ required: true, message: '電話番号を入力してください' }]}
          >
            <PhoneInput placeholder="03-1234-5678" />
          </Form.Item>

          <Form.Item
            name="agencyTier"
            label="代理店階層"
            initialValue={1}
            rules={[{ required: true }]}
          >
            <NumericInput min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="commissionRate"
            label="手数料レート（%）"
            initialValue={30}
            rules={[{ required: true, message: '手数料レートを入力してください' }]}
          >
            <NumericInput
              min={0}
              max={100}
              step={0.5}
              precision={2}
              style={{ width: '100%' }}
              placeholder="30"
            />
          </Form.Item>

          <Form.Item name="note" label="運営側メモ">
            <Input.TextArea rows={3} placeholder="内部メモ（代理店には表示されません）" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'address',
      label: '住所情報 *',
      children: (
        <>
          <Form.Item
            name="postalCode"
            label="郵便番号"
            rules={[{ required: true, message: '郵便番号を入力してください' }]}
          >
            <PostalCodeInput placeholder="100-0001" />
          </Form.Item>

          <Form.Item
            name="fullAddress"
            label="住所"
            rules={[{ required: true, message: '住所を入力してください' }]}
            extra="都道府県から番地まで入力してください（例：東京都千代田区千代田1-1-1）"
          >
            <Input placeholder="東京都千代田区千代田1-1-1" />
          </Form.Item>

          <Form.Item name="building" label="建物名・部屋番号">
            <Input placeholder="千代田ビル3F" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'bank',
      label: '支払先情報 *',
      children: (
        <>
          <Form.Item
            name="bankCode"
            label="銀行コード（任意）"
            rules={[
              {
                pattern: /^\d{4}$/,
                message: '銀行コードは4桁の数字で入力してください'
              }
            ]}
            extra="4桁の銀行コード（例：0001 = みずほ銀行）"
          >
            <Input placeholder="0001" maxLength={4} />
          </Form.Item>

          <Form.Item
            name="bankName"
            label="銀行名"
            rules={[{ required: true, message: '銀行名を入力してください' }]}
          >
            <Input placeholder="みずほ銀行" />
          </Form.Item>

          <Form.Item
            name="bankBranchName"
            label="支店名"
            rules={[{ required: true, message: '支店名を入力してください' }]}
          >
            <Input placeholder="本店" />
          </Form.Item>

          <Form.Item
            name="bankAccountType"
            label="口座種別"
            initialValue="ordinary"
            rules={[{ required: true, message: '口座種別を選択してください' }]}
          >
            <Radio.Group>
              <Radio value="ordinary">普通</Radio>
              <Radio value="current">当座</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="bankAccountNumber"
            label="口座番号"
            rules={[{ required: true, message: '口座番号を入力してください' }]}
          >
            <Input placeholder="1234567" />
          </Form.Item>

          <Form.Item
            name="bankAccountHolder"
            label="口座名義"
            rules={[{ required: true, message: '口座名義を入力してください' }]}
          >
            <Input placeholder="カブシキガイシャXXX" />
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="新規代理店作成"
        open={open}
        onCancel={() => {
          form.resetFields();
          onCancel();
        }}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending}
        okText="作成"
        cancelText="キャンセル"
        width={700}
      >
        <Form form={form} layout="vertical">
          <Tabs items={tabItems} destroyInactiveTabPane={false} />
        </Form>
      </Modal>

      {/* 開発環境用: メールプレビューモーダル */}
      {isDevelopment && (
        <DevEmailPreviewModal
          open={emailPreviewOpen}
          onClose={() => setEmailPreviewOpen(false)}
          emailData={devEmailData}
        />
      )}
    </>
  );
}
